// core/services/creatorsHubService.ts — async service for the Creators' Hub.
//
// Port of creators-hub behavior from legacy src/scripts/data/creators-hub.js.
//
// Data-flow pattern (T4 reference):
//   - Reads from useCreatorsHubStore (Zustand).
//   - All mutations are async (Promise<T>) and update the store.
//   - Components must not import this directly; use hooks/useCreatorsHub.ts.
//
// _resetForTest() resets all mutable state in the store so tests are
// independent without reloading the module.

import type { HubDiscussion, HubCollaboration, HubInvite, HubEvent } from '@/core/models/creators-hub';
import { useCreatorsHubStore } from '@/core/stores/creatorsHubStore';

function getStore() {
  return useCreatorsHubStore.getState();
}

export const creatorsHubService = {
  /** Return all hub discussions in seed order. */
  async discussions(): Promise<HubDiscussion[]> {
    return getStore().discussions;
  },

  /** Return all collaborations. */
  async collaborations(): Promise<HubCollaboration[]> {
    return getStore().collaborations;
  },

  /** Return all pending invites. */
  async invites(): Promise<HubInvite[]> {
    return getStore().invites;
  },

  /** Return all upcoming events. */
  async events(): Promise<HubEvent[]> {
    return getStore().events;
  },

  /** Return the featured creator handles. */
  async featuredCreatorHandles(): Promise<string[]> {
    return getStore().featuredCreatorHandles;
  },

  /**
   * Toggle the current user's interest in a collaboration.
   * Flips `youInterested` and adjusts the `interested` count by ±1.
   * No-op if id not found.
   */
  async toggleInterest(id: string): Promise<void> {
    getStore().toggleInterest(id);
  },

  /**
   * Accept a creator invite.
   * Removes the invite from the list. No-op if id not found.
   */
  async acceptInvite(id: string): Promise<void> {
    getStore().removeInvite(id);
  },

  /**
   * Decline a creator invite.
   * Removes the invite from the list. No-op if id not found.
   */
  async declineInvite(id: string): Promise<void> {
    getStore().removeInvite(id);
  },

  /**
   * Reset all mutable hub state back to the seed (for test isolation).
   * Only for use in test beforeEach; not exposed via hooks.
   */
  _resetForTest(): void {
    getStore().reset();
  },
} as const;
